// Keeps the frame on the visible part of the screen while the keyboard is up
// (a phone), and lets a tap on the status bar take the screen to its top.
//
// Every screen scrolls inside its own box (the frame is the window's height),
// so on iOS the keyboard would push the whole page up under the status bar
// and the post box with it. Instead the frame is laid over the visible part
// of the screen (visualViewport) for as long as the phone layout is on:
// - The frame is `position: absolute` at the visible part's page position and
//   height (`html[data-keyboard-frame]`, phone.css), not `fixed`: in a fixed
//   box iOS's WebKit did not redraw the caret where the field moved
//   (2026-09-28, 2026-10-01). The shell inside takes the frame's height (it
//   is `100dvh` elsewhere, and iOS's dvh does not count the keyboard: the post
//   box was cut off under it). The frame holds the layout (`contain`), so the
//   fixed pieces (the Dock, its menu) keep to it and move with it.
// - Laid over the visible part from the start, not only while the keyboard is
//   up: switching to a box on opening blacked the chat out for about 75 ms
//   (2026-10-01), and following the page with a fixed bar shook for frames.
// - While the keyboard is up the rows above the screen's main scrolling box
//   (its header) go out of sight above (the shell inside the frame moves up
//   and grows by that much; the frame itself stays on the visible part, so
//   what is fixed in it is placed by the visible screen's coordinates), as
//   iOS did when the page scrolled; not when the field being written is up
//   there (a list's filter).
// - When the frame's height changes, the caret is brought into sight in its
//   own box (only that box moves). A chat runs bottom up and keeps the rows
//   above the post box where they were by itself. When only the visible part
//   moved, nothing inside is touched (pulling back to the caret moved the
//   screen against the finger, 2026-10-01).
// - While the keyboard is up, a finger moving what cannot scroll would move
//   the visible part itself: that move is stopped.
// - While the keyboard is up the page itself stays at its top. iOS scrolls
//   the page (about 340 px) to show a text field it takes focus in, though
//   the frame already shows it; left there, closing the keyboard scrolled the
//   page back in iOS's own motion, which no script sees (the scroll reads its
//   end at once): the post box slid down through Safari's bar and the Dock at
//   the top slid in from above (2026-10-18; the old web's editor took the
//   page back to the top by itself). At the top, the screen stays still while
//   the keyboard goes down and is laid out once when it is gone, as the old
//   web did.
// - iOS takes only the page to its top on a status bar tap, never a box: the
//   page is kept scrollable by 1 px and parked at 1; when iOS brings it to 0
//   the screen's scrolling box goes to its top instead. A finger pulling the
//   page also brings it to 0, so not while touching or just after. The
//   router's own reset to the top on every move is not a tap either.
// - While a host's own keyboard is up (a native app's post box,
//   `html[data-host-keyboard]`), nothing is laid out for a keyboard: the
//   frame keeps the whole view and the host's box sits on its keyboard.
// What it costs: Safari's bottom bar does not fold on scroll (the person
// agreed, 2026-10-01; the home screen app has no bar).
import type { AnyRouter } from "@tanstack/react-router";
import { useLayoutEffect } from "react";
import {
  HOST_KEYBOARD,
  HOST_KEYBOARD_EVENT,
  HOST_KEYBOARD_HEIGHT,
} from "../shell/outside/composer-host";
import { TO_HEAD_EVENT } from "../shell/phone";
import { phoneTopInset, TOP_DOCK_CLEARANCE } from "./top-dock";
import { KEYBOARD_MIN_PX } from "./typing";

/** A field of the page has the focus (the keyboard, if up, is the page's). */
function pageFieldFocused(): boolean {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el instanceof HTMLTextAreaElement) return true;
  return (
    el instanceof HTMLInputElement &&
    ![
      "checkbox",
      "radio",
      "button",
      "submit",
      "reset",
      "file",
      "range",
      "color",
    ].includes(el.type)
  );
}

const ATTR = "data-keyboard-frame";
/** On while the header rows are out of sight above (phone.css). */
const SHIFTED = "data-keyboard-shifted";
const PROBE = "data-scroll-top-probe";
/** A page at 0 within this long after a finger left is the finger's. */
const TOUCH_QUIET_MS = 800;
/** Back to 1 px after iOS's scroll to the top has finished. */
const REARM_MS = 500;
/** Room kept between the caret and its box's edge when bringing it into sight. */
const CARET_MARGIN_PX = 16;

/**
 * The screen's main scrolling box: the largest one that can scroll in what
 * lies over the screen (overlay.tsx), else in the panel.
 */
function mainScroller({
  evenShort,
}: {
  evenShort?: boolean;
} = {}): HTMLElement | null {
  const main =
    document.querySelector<HTMLElement>("[data-phone-overlay]") ??
    document.querySelector<HTMLElement>("[data-me-main]");
  if (!main) return null;
  let best: HTMLElement | null = null;
  let area = 0;
  for (const el of main.querySelectorAll<HTMLElement>("*")) {
    if (
      el.tagName === "TEXTAREA" ||
      (!evenShort && el.scrollHeight <= el.clientHeight + 1)
    )
      continue;
    const y = getComputedStyle(el).overflowY;
    if (y !== "auto" && y !== "scroll") continue;
    const a = el.clientWidth * el.clientHeight;
    if (a > area) {
      area = a;
      best = el;
    }
  }
  return best;
}

/**
 * Takes a box to the top of what it holds. A chat runs bottom up
 * (`column-reverse`: 0 is its end), so its top is the most negative place.
 */
function scrollToHead(box: HTMLElement | null): void {
  if (!box) return;
  // A box that goes to its head by itself (a long chat) says so.
  if (!box.dispatchEvent(new CustomEvent(TO_HEAD_EVENT, { cancelable: true })))
    return;
  const reverse = getComputedStyle(box).flexDirection === "column-reverse";
  box.scrollTo({
    top: reverse ? -(box.scrollHeight - box.clientHeight) : 0,
    behavior: "smooth",
  });
}

/** The scrolling box that holds `el` (the nearest one that can scroll). */
function scrollerOf(el: Element): HTMLElement | null {
  let box = el.parentElement;
  while (box && box !== document.body) {
    const y = getComputedStyle(box).overflowY;
    if ((y === "auto" || y === "scroll") && box.scrollHeight > box.clientHeight)
      return box;
    box = box.parentElement;
  }
  return null;
}

/**
 * Brings the place being written (the caret) into its box's sight, moving
 * only that box. A chat runs bottom up and keeps its end by itself; its box is
 * left alone.
 */
function revealCaret(): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return;
  let caret: { top: number; bottom: number } | undefined;
  if (active.isContentEditable) {
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const rects = range.getClientRects();
    caret = rects[rects.length - 1] ?? range.getBoundingClientRect();
    // An empty line has no rectangle of its own: its line's.
    if (caret.top === 0 && caret.bottom === 0) {
      const node = range.startContainer;
      const line = node instanceof Element ? node : node.parentElement;
      caret = line?.getBoundingClientRect();
    }
  } else if (active.tagName === "TEXTAREA" || active.tagName === "INPUT") {
    caret = active.getBoundingClientRect();
  }
  if (!caret || (caret.top === 0 && caret.bottom === 0)) return;
  const box = scrollerOf(active);
  if (!box || getComputedStyle(box).flexDirection === "column-reverse") return;
  const frame = box.getBoundingClientRect();
  if (caret.bottom > frame.bottom - CARET_MARGIN_PX)
    box.scrollTop += caret.bottom - frame.bottom + CARET_MARGIN_PX;
  else if (caret.top < frame.top + CARET_MARGIN_PX)
    box.scrollTop -= frame.top - caret.top + CARET_MARGIN_PX;
}

/**
 * How far above the screen's main scrolling box the frame begins (the Dock at
 * the top, a list's title, a row above the box), to be put out of sight while
 * the keyboard is up. The Dock at the top floats over the box: its room counts
 * even where the box begins under it. Nothing when the field being written is
 * up there (a list's filter): it must stay in sight.
 */
function measureAbove(): number {
  // A chat of a few rows does not scroll yet: its box still counts.
  const box = mainScroller({ evenShort: true });
  const frame = document.querySelector<HTMLElement>("[data-me-frame]");
  if (!box || !frame) return 0;
  const root =
    document.querySelector<HTMLElement>("[data-phone-overlay]") ??
    document.querySelector<HTMLElement>("[data-me-main]");
  // With nothing on it, it takes no room (phone.css).
  const dock = root?.querySelector<HTMLElement>(
    "[data-top-dock]:has(button, a)",
  );
  const top = Math.max(
    box.getBoundingClientRect().top,
    dock
      ? dock.getBoundingClientRect().top + TOP_DOCK_CLEARANCE + phoneTopInset()
      : 0,
  );
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    active !== document.body &&
    !box.contains(active) &&
    active.getBoundingClientRect().bottom <= top
  )
    return 0;
  // A field in a drawer rising from the bottom: the drawer sits on the
  // keyboard by itself, and the screen's header under it is not in the way.
  // Moved up with the shell, the drawer's top went out of sight and its
  // bottom left a gap over the keyboard (a phone opened sideways, its
  // visible part short: the person, 2026-10-30).
  if (
    active instanceof HTMLElement &&
    active.closest("[data-turn-drawer], [data-bottom-drawer]")
  )
    return 0;
  // Under an app's status bar (the page runs under it) its room stays: what
  // goes up stops below it, not behind it (the agents' list's search field
  // went up under the status bar, 2026-10-29).
  return Math.max(
    0,
    Math.round(top - frame.getBoundingClientRect().top - phoneTopInset()),
  );
}

/** Whether something above `target` scrolls up and down under a finger. */
function insideScroller(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.body) {
    if (el instanceof HTMLElement && el.scrollHeight > el.clientHeight) {
      const y = getComputedStyle(el).overflowY;
      if (y === "auto" || y === "scroll") return true;
    }
    el = el.parentElement;
  }
  return false;
}

/** Keeps the frame on the visible part of the screen while `active`. */
export function useKeyboardFrame(active: boolean, router: AnyRouter): void {
  // frontend-development: not a fetch; writes the frame's place before paint.
  useLayoutEffect(() => {
    const vv = window.visualViewport;
    if (!active || !vv) return;
    const html = document.documentElement;
    /**
     * The host's keyboard is up: none of the web's (its own box sits on it).
     * Not while a field of the page has the focus: the keyboard is then the
     * page's (a drawer's field opened over the host's box, which went away
     * still saying its keyboard was up: the field lay under the keyboard).
     */
    const hosts = () => html.hasAttribute(HOST_KEYBOARD) && !pageFieldFocused();
    /**
     * The keyboard over this window as the host measures it (null: the host
     * says nothing, and the visible part tells). Two windows of the app side
     * by side both lie under it; the visible part shrank in the one not
     * written in, and not in the one written in (the iPhone Duo, 2026-10-30).
     */
    const measured = () => {
      const v = html.getAttribute(HOST_KEYBOARD_HEIGHT);
      return v === null ? null : Number(v);
    };
    const keyboardOpen = () => {
      if (hosts()) return false;
      const by = measured();
      return by === null
        ? vv.height < html.clientHeight - KEYBOARD_MIN_PX
        : by > KEYBOARD_MIN_PX && pageFieldFocused();
    };
    /** The visible part's height, the whole view's under the host's keyboard. */
    const seen = () => {
      if (hosts()) return html.clientHeight;
      const by = measured();
      if (by === null) return vv.height;
      return keyboardOpen() ? html.clientHeight - by : html.clientHeight;
    };
    /** The header rows put out of sight while the keyboard is up (measured once per opening). */
    let above: number | null = null;
    let shift = 0;
    /** The frame's height last written: the content is placed again only when it changes. */
    let height: number | null = null;
    const write = () => {
      // In page coordinates: the frame is in the page, not fixed.
      // The frame stays on the visible part; the shell inside moves up by
      // the shift (phone.css), so fixed pieces placed by the visible screen's
      // coordinates are not off by it.
      // The visible part's top even with the host's measure: iOS may move
      // it in a window not written in, and the frame stays on what is seen.
      const pageTop = hosts()
        ? window.scrollY
        : (vv.pageTop ?? window.scrollY + vv.offsetTop);
      html.style.setProperty("--keyboard-frame-top", `${pageTop}px`);
      html.style.setProperty("--keyboard-frame-height", `${seen()}px`);
      html.style.setProperty("--keyboard-frame-shift", `${shift}px`);
      html.toggleAttribute(SHIFTED, shift > 0);
    };
    /**
     * Lays the frame over the visible part again. While the keyboard is up
     * the header rows go out of sight above it, as iOS did when the page
     * itself scrolled. When the frame's height changed, the caret is brought
     * into sight in its box; when only the visible part moved (a finger
     * scrolling), nothing inside is touched: pulling back to the caret then
     * moved the screen against the finger.
     */
    const place = () => {
      const before = height;
      if (!keyboardOpen()) {
        above = null;
        shift = 0;
      } else if (above === null) {
        shift = 0;
        write();
        above = measureAbove();
        shift = above;
      }
      write();
      height = seen() + shift;
      if (before !== null && before !== height) revealCaret();
    };
    let armed = false;
    let touches = 0;
    let lastTouch = 0;
    let movedAt = Number.NEGATIVE_INFINITY;
    const arm = () => {
      if (armed) return;
      html.setAttribute(PROBE, "");
      window.scrollTo(0, 1);
      armed = true;
    };
    const disarm = () => {
      if (!armed) return;
      armed = false;
      html.removeAttribute(PROBE);
    };
    let rearm = 0;
    const rearmLater = () => {
      window.clearTimeout(rearm);
      rearm = window.setTimeout(() => {
        if (armed && window.scrollY <= 0) window.scrollTo(0, 1);
      }, REARM_MS);
    };
    /** The page back to its top while the keyboard is up (not under a finger). */
    const keepPageAtTop = () => {
      if (touches > 0 || window.scrollY <= 0 || !keyboardOpen()) return false;
      window.scrollTo(0, 0);
      return true;
    };
    const onWindowScroll = () => {
      if (keepPageAtTop()) return;
      write();
      if (!armed || touches > 0 || window.scrollY > 0) return;
      // The router took the page to the top after a move: park it again.
      if (Date.now() - movedAt < REARM_MS) {
        window.scrollTo(0, 1);
        // Placed again at once: left where the reset put it, the frame stood
        // 1 px off until the next scroll, then dropped back (an overlay's
        // history entry resets the scroll: the Dock's menu shifted down as
        // it closed, the person 2026-10-18).
        write();
        return;
      }
      if (Date.now() - lastTouch >= TOUCH_QUIET_MS)
        scrollToHead(mainScroller());
      rearmLater();
    };
    const onViewport = () => {
      // Off first, so the page growing shorter is not read as a tap.
      if (keyboardOpen()) disarm();
      keepPageAtTop();
      place();
      if (!keyboardOpen()) arm();
    };
    const onTouch = (e: TouchEvent) => {
      touches = e.touches.length;
      lastTouch = Date.now();
    };
    const onTouchMove = (e: TouchEvent) => {
      if (keyboardOpen() && !insideScroller(e.target)) e.preventDefault();
    };
    const onNavigate = () => {
      movedAt = Date.now();
    };
    html.setAttribute(ATTR, "");
    place();
    if (!keyboardOpen()) arm();
    vv.addEventListener("resize", onViewport);
    vv.addEventListener("scroll", onViewport);
    window.addEventListener(HOST_KEYBOARD_EVENT, onViewport);
    // Whose keyboard it is follows the focus too (`hosts`, and with the
    // host's measure, whether a field of this window is written in).
    document.addEventListener("focusin", onViewport);
    document.addEventListener("focusout", onViewport);
    window.addEventListener("scroll", onWindowScroll, { passive: true });
    const offRendered = router.subscribe("onRendered", onNavigate);
    document.addEventListener("touchstart", onTouch, { passive: true });
    document.addEventListener("touchend", onTouch, { passive: true });
    document.addEventListener("touchcancel", onTouch, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      vv.removeEventListener("resize", onViewport);
      vv.removeEventListener("scroll", onViewport);
      document.removeEventListener("focusin", onViewport);
      document.removeEventListener("focusout", onViewport);
      window.removeEventListener(HOST_KEYBOARD_EVENT, onViewport);
      document.removeEventListener("focusin", onViewport);
      document.removeEventListener("focusout", onViewport);
      window.removeEventListener("scroll", onWindowScroll);
      offRendered();
      document.removeEventListener("touchstart", onTouch);
      document.removeEventListener("touchend", onTouch);
      document.removeEventListener("touchcancel", onTouch);
      document.removeEventListener("touchmove", onTouchMove);
      window.clearTimeout(rearm);
      disarm();
      html.removeAttribute(ATTR);
      html.style.removeProperty("--keyboard-frame-top");
      html.style.removeProperty("--keyboard-frame-height");
      html.style.removeProperty("--keyboard-frame-shift");
      html.removeAttribute(SHIFTED);
    };
  }, [active, router]);
}
